import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { OccurrenceStepEnum } from '../../../enums/occurrenceStep';
import type { Occurrence } from '../../../models/occurrence';
import EstimatedRepairTimeDialog from '../../kanban/dialogs/estimatedRepairTimeDialog';

type ChangeStep = (
    occurrence: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
) => Promise<Occurrence | null | undefined>;

type Save = (occurrence: Occurrence) => Promise<Occurrence | false>;

const useRepairGuard = (changeStep: ChangeStep, save: Save) => {
    const intl = useIntl();
    const [pending, setPending] = useState<{ occurrence: Occurrence; fromStepId: number } | null>(null);
    const [loading, setLoading] = useState(false);
    const resolveRef = useRef<((value: Occurrence | null | undefined) => void) | null>(null);
    const rejectRef = useRef<((reason?: unknown) => void) | null>(null);

    const clearPending = () => {
        resolveRef.current = null;
        rejectRef.current = null;
        setPending(null);
    };

    const guardedChangeStep: ChangeStep = (occurrence, fromStepId, to) => {
        if (to !== OccurrenceStepEnum.REPAIR) {
            return changeStep(occurrence, fromStepId, to);
        }
        return new Promise((resolve, reject) => {
            resolveRef.current = resolve;
            rejectRef.current = reject;
            setPending({ occurrence, fromStepId });
        });
    };

    const dialog = (
        <EstimatedRepairTimeDialog
            showDialog={!!pending}
            loading={loading}
            onClose={() => {
                resolveRef.current?.(undefined);
                clearPending();
            }}
            onConfirm={async estimateTimeRepair => {
                if (!pending) {
                    return;
                }
                setLoading(true);
                try {
                    const saved = await save({ ...pending.occurrence, estimateTimeRepair });
                    if (!saved) {
                        rejectRef.current?.(
                            new Error(intl.formatMessage({ id: 'intl-msg:kanban.kanban.saveEstimatedRepairTimeError' }))
                        );
                        return;
                    }
                    const result = await changeStep(pending.occurrence, pending.fromStepId, OccurrenceStepEnum.REPAIR);
                    resolveRef.current?.(result);
                } catch (error) {
                    rejectRef.current?.(error);
                } finally {
                    setLoading(false);
                    clearPending();
                }
            }}
        />
    );

    return { guardedChangeStep, repairDialog: dialog };
};

export default useRepairGuard;
